import type {
  TechniqueArchiveCatalogEntry,
  TechniqueArchiveView,
  TechniqueProgressionResult,
} from "@ninja/contracts";
import {
  RARITY_PRESENTATION,
  TECHNIQUE_LEVELS,
  techniqueProgressionRequirement,
  type TechniqueLevelParameters,
} from "@ninja/domain";
import { TECHNIQUES } from "@ninja/game-content";
import { TECHNIQUE_ICONS } from "@ninja/asset-catalog";
import { renderNinjaOutfitImage } from "./NinjaOutfitPortrait.js";
import { SectionTabs } from "./SectionTabs.js";

export interface TechniqueArchiveNinja {
  id: string;
  name: string;
  appearance_id: string;
  rarity: string;
  rank: string;
  level: number;
  life_status?: string | undefined;
  activity_lock?: string | null | undefined;
}

type ArchiveRequest = <T>(path: string, init?: RequestInit) => Promise<T>;

function escapeHtml(value: unknown): string {
  return String(value ?? "").replace(
    /[&<>"']/g,
    (character) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        character
      ] ?? character,
  );
}
function rarityColor(rarity: string): string {
  return (
    RARITY_PRESENTATION[rarity as keyof typeof RARITY_PRESENTATION]?.color ??
    RARITY_PRESENTATION.common.color
  );
}
function rarityLabel(rarity: string): string {
  return (
    RARITY_PRESENTATION[rarity as keyof typeof RARITY_PRESENTATION]?.label ??
    rarity
  );
}
function familyLabel(family: string): string {
  return family
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}
function rarityForLevel(level: number): string {
  if (level <= 4) return "uncommon";
  if (level <= 7) return "rare";
  if (level <= 9) return "epic";
  return "legendary";
}
function techniqueIcon(techniqueId: string, className = ""): string {
  const icon = TECHNIQUE_ICONS[techniqueId as keyof typeof TECHNIQUE_ICONS];
  if (!icon) return `<span class="technique-archive-icon-fallback">J</span>`;
  const path =
    "fallbackPath" in icon && icon.fallbackPath ? icon.fallbackPath : icon.path;
  return `<img class="${escapeHtml(className)}" src="/${escapeHtml(path)}" width="64" height="64" alt="" loading="lazy" />`;
}
function ninjaPortrait(ninja: TechniqueArchiveNinja | undefined): string {
  if (!ninja) return `<span class="archive-ninja-placeholder">N</span>`;
  return renderNinjaOutfitImage(ninja.appearance_id, ninja.name);
}
function targetingLabel(targeting: string): string {
  return (
    (
      {
        frontal_cone: "Frontal cone",
        projectile: "Projectile",
        circle: "Circular area",
        point_area: "Point area",
        ally_injured: "Injured ally",
        ally_injured_or_threatened: "Injured or threatened ally",
        single_hostile: "Single enemy",
        self: "Self",
        reactive_counter: "Reactive counter",
      } as Record<string, string>
    )[targeting] ?? familyLabel(targeting)
  );
}
function seconds(ms: number): string {
  const value = ms / 1000;
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
function otherMechanics(value: TechniqueLevelParameters): string {
  const details = [`ATK coefficient ${value.attackCoefficient.toFixed(2)}`];
  if (value.coneDegrees !== undefined)
    details.push(`Cone ${value.coneDegrees} degrees`);
  if (value.radius !== undefined) details.push(`Radius ${value.radius}`);
  if (value.minimumTargets !== undefined)
    details.push(`Min targets ${value.minimumTargets}`);
  if (value.injuredHpThreshold !== undefined)
    details.push(`Heal below ${value.injuredHpThreshold}% HP`);
  if (value.impactDelayMs !== undefined)
    details.push(`Impact delay ${seconds(value.impactDelayMs)}s`);
  if (value.healBase !== undefined && value.healCoefficient !== undefined)
    details.push(
      `Heal ${value.healBase} + technique ATK × ${value.healCoefficient.toFixed(2)}`,
    );
  if (value.shieldBase !== undefined && value.shieldCoefficient !== undefined)
    details.push(
      `Shield ${value.shieldBase} + technique ATK × ${value.shieldCoefficient.toFixed(2)}`,
    );
  if (value.shieldDurationMs !== undefined)
    details.push(`Shield ${seconds(value.shieldDurationMs)}s`);
  if (value.damageReduction !== undefined)
    details.push(
      "Damage reduction " + Math.round(value.damageReduction * 100) + "%",
    );
  if (value.defenseWindowMs !== undefined)
    details.push("Guard " + seconds(value.defenseWindowMs) + "s");
  if (value.counterDamageBonus !== undefined)
    details.push(
      "Counter bonus +" + Math.round(value.counterDamageBonus * 100) + "%",
    );
  if (value.control) {
    details.push(`Fear ${seconds(value.control.fearMs)}s`);
    details.push(`Debuff ${seconds(value.control.debuffMs)}s`);
    details.push(
      `${Math.round(value.control.damageReduction * 100)}% damage reduction`,
    );
  }
  return details.join(" | ");
}

export class TechniqueArchivePanel {
  private view: TechniqueArchiveView | null = null;
  private detailTabs: SectionTabs | null = null;
  private selectedNinjaId: string;
  private selectedTechniqueId = Object.keys(TECHNIQUES)[0] ?? "";
  private pendingKeys = new Map<string, string>();
  private noticeText = "";
  private ninjaSearch = "";
  private techniqueSearch = "";
  private familyFilter = "";
  private levelRarityFilter = "";
  private loadGeneration = 0;

  constructor(
    private readonly root: HTMLElement,
    private readonly ninjas: readonly TechniqueArchiveNinja[],
    private readonly request: ArchiveRequest,
  ) {
    this.selectedNinjaId =
      ninjas.find(
        (ninja) => ninja.life_status === "available" && !ninja.activity_lock,
      )?.id ??
      ninjas[0]?.id ??
      "";
  }

  async mount(): Promise<void> {
    this.renderLoading();
    await this.load();
  }
  private renderLoading(): void {
    this.detailTabs?.destroy();
    this.detailTabs = null;
    this.root.innerHTML = `<div class="page-heading"><div><p class="eyebrow">VILLAGE KNOWLEDGE</p><h1>Technique Archive</h1><p class="muted">Choose a ninja and technique to view its mastery path.</p></div></div><div class="empty-state">Loading the Archive...</div>`;
  }
  private async load(): Promise<void> {
    if (!this.selectedNinjaId) {
      this.detailTabs?.destroy();
      this.detailTabs = null;
      this.root.innerHTML = `<div class="page-heading"><div><p class="eyebrow">VILLAGE KNOWLEDGE</p><h1>Technique Archive</h1></div></div><div class="empty-state">Recruit a ninja before using the Archive.</div>`;
      return;
    }
    const generation = ++this.loadGeneration;
    this.root.classList.add("technique-archive-loading");
    try {
      const view = await this.request<TechniqueArchiveView>(
        `/technique-archive?ninjaId=${encodeURIComponent(this.selectedNinjaId)}`,
      );
      if (generation !== this.loadGeneration) return;
      this.view = view;
      this.root.classList.remove("technique-archive-loading");
      if (
        !view.techniques.some(
          (entry) => entry.techniqueId === this.selectedTechniqueId,
        )
      )
        this.selectedTechniqueId = view.techniques[0]?.techniqueId ?? "";
      this.render();
    } catch (error) {
      if (generation !== this.loadGeneration) return;
      this.root.classList.remove("technique-archive-loading");
      this.detailTabs?.destroy();
      this.detailTabs = null;
      this.root.innerHTML = `<div class="page-heading"><div><p class="eyebrow">VILLAGE KNOWLEDGE</p><h1>Technique Archive</h1></div></div><div class="empty-state">${escapeHtml((error as Error).message)}</div>`;
    }
  }
  private selectedNinja(): TechniqueArchiveNinja | undefined {
    return this.ninjas.find((ninja) => ninja.id === this.selectedNinjaId);
  }
  private ninjaOptions(): string {
    const query = this.ninjaSearch.trim().toLocaleLowerCase();
    const matches = this.ninjas.filter((ninja) =>
      ninja.name.toLocaleLowerCase().includes(query),
    );
    const selected = this.selectedNinja();
    if (selected && !matches.some((ninja) => ninja.id === selected.id))
      matches.unshift(selected);
    return matches
      .map((ninja) => {
        const selected = ninja.id === this.selectedNinjaId ? "selected" : "";
        const status =
          ninja.life_status && ninja.life_status !== "available"
            ? ` | ${ninja.life_status}`
            : ninja.activity_lock
              ? " | On assignment"
              : "";
        return `<option value="${escapeHtml(ninja.id)}" ${selected}>${escapeHtml(ninja.name)} | Lv ${ninja.level}${escapeHtml(status)}</option>`;
      })
      .join("");
  }
  private render(): void {
    this.detailTabs?.destroy();
    this.detailTabs = null;
    const view = this.view;
    if (!view) return;
    const ninja = this.selectedNinja();
    const familyOptions = [
      ...new Set(view.techniques.map((entry) => entry.family)),
    ]
      .map(
        (family) =>
          `<option value="${escapeHtml(family)}" ${family === this.familyFilter ? "selected" : ""}>${escapeHtml(familyLabel(family))}</option>`,
      )
      .join("");
    const availability = view.ninja.canProgress
      ? `<span class="archive-status archive-status-ready">Available in village</span>`
      : `<span class="archive-status archive-status-blocked">${escapeHtml(view.ninja.unavailableReason)}</span>`;
    this.root.innerHTML = `
      <div class="page-heading technique-archive-heading">
        <div><p class="eyebrow">VILLAGE KNOWLEDGE</p><h1>Technique Archive</h1><p class="muted">Choose a ninja, inspect a technique, then teach or improve it.</p></div>
        <span class="season-badge">ARCHIVE LV ${view.archive.level} / ${view.archive.maxLevel}</span>
      </div>
      <section class="technique-archive-panel">
        <div class="archive-selected-ninja">
          <div class="archive-selected-ninja-portrait">${ninjaPortrait(ninja)}</div>
          <div class="archive-ninja-picker">
            <label for="archive-ninja-search">Find ninja<input id="archive-ninja-search" type="search" placeholder="Search by name" autocomplete="off" value="${escapeHtml(this.ninjaSearch)}" /></label>
            <label for="archive-ninja">Selected ninja<select id="archive-ninja">${this.ninjaOptions()}</select></label>
          </div>
          <div class="archive-selected-ninja-summary">
            <strong>${escapeHtml(ninja?.name ?? view.ninja.name)}</strong>
            <span>${escapeHtml(rarityLabel(ninja?.rarity ?? view.ninja.rarity))} | ${escapeHtml(ninja?.rank ?? "")} | Lv ${ninja?.level ?? ""}</span>
            ${availability}
          </div>
        </div>
        <div class="archive-compact-note">Exclusive scrolls are consumed from the Warehouse. Ryo and Energy are free in this MVP.</div>
        <div class="technique-archive-workspace">
          <section class="archive-technique-library" aria-label="Technique library">
            <div class="archive-library-toolbar">
              <label>Search<input id="archive-search" type="search" placeholder="Technique name" autocomplete="off" value="${escapeHtml(this.techniqueSearch)}" /></label>
              <label>Family<select id="archive-family"><option value="">All families</option>${familyOptions}</select></label>
              <label>Level rarity<select id="archive-level-rarity"><option value="">All</option><option value="uncommon" ${this.levelRarityFilter === "uncommon" ? "selected" : ""}>Uncommon</option><option value="rare" ${this.levelRarityFilter === "rare" ? "selected" : ""}>Rare</option><option value="epic" ${this.levelRarityFilter === "epic" ? "selected" : ""}>Epic</option><option value="legendary" ${this.levelRarityFilter === "legendary" ? "selected" : ""}>Legendary</option></select></label>
            </div>
            <div class="archive-technique-grid" id="archive-technique-grid">${view.techniques.map((entry) => this.renderTechniqueIcon(entry)).join("")}</div>
            <div class="archive-icon-legend"><span><i class="archive-new-indicator">NEW</i> Ready to learn</span><span><i class="archive-upgrade-indicator">&#8593;</i> Ready to improve</span></div>
            <p id="archive-filter-empty" class="archive-filter-empty" hidden>No techniques match these filters.</p>
          </section>
          <section class="archive-technique-details" id="archive-technique-details" aria-live="polite">${this.renderTechniqueDetails()}</section>
        </div>
        <p id="archive-notice" class="notice" role="status" aria-live="polite">${escapeHtml(this.noticeText)}</p>
      </section>`;

    const detailRoot = this.root.querySelector<HTMLElement>(
      "#archive-technique-details",
    );
    if (detailRoot) this.detailTabs = new SectionTabs(detailRoot);

    this.root
      .querySelector<HTMLInputElement>("#archive-ninja-search")
      ?.addEventListener("input", (event) => {
        this.ninjaSearch = (event.currentTarget as HTMLInputElement).value;
        const select =
          this.root.querySelector<HTMLSelectElement>("#archive-ninja");
        if (select) {
          const current = this.selectedNinjaId;
          select.innerHTML = this.ninjaOptions();
          select.value = current;
        }
      });
    this.root
      .querySelector<HTMLSelectElement>("#archive-ninja")
      ?.addEventListener("change", (event) => {
        this.selectedNinjaId = (event.currentTarget as HTMLSelectElement).value;
        this.noticeText = "";
        void this.load();
      });
    this.root
      .querySelector<HTMLInputElement>("#archive-search")
      ?.addEventListener("input", (event) => {
        this.techniqueSearch = (event.currentTarget as HTMLInputElement).value;
        this.applyFilters();
      });
    this.root
      .querySelector<HTMLSelectElement>("#archive-family")
      ?.addEventListener("change", (event) => {
        this.familyFilter = (event.currentTarget as HTMLSelectElement).value;
        this.applyFilters();
      });
    this.root
      .querySelector<HTMLSelectElement>("#archive-level-rarity")
      ?.addEventListener("change", (event) => {
        this.levelRarityFilter = (
          event.currentTarget as HTMLSelectElement
        ).value;
        this.applyFilters();
      });
    this.root
      .querySelectorAll<HTMLButtonElement>("[data-select-technique]")
      .forEach((button) =>
        button.addEventListener("click", () => {
          this.selectedTechniqueId = button.dataset.selectTechnique ?? "";
          this.render();
        }),
      );
    this.root
      .querySelector<HTMLButtonElement>("#archive-progress-technique")
      ?.addEventListener("click", () => {
        const entry = view.techniques.find(
          (item) => item.techniqueId === this.selectedTechniqueId,
        );
        if (entry?.next) this.confirmProgression(entry);
      });
    this.applyFilters();
  }
  private renderTechniqueIcon(entry: TechniqueArchiveCatalogEntry): string {
    const learned = entry.currentLevel !== null;
    const rarity = rarityForLevel(entry.currentLevel ?? 1);
    const canProgress = Boolean(
      entry.canAdvance && this.view?.ninja.canProgress,
    );
    const indicator =
      canProgress && !learned
        ? `<span class="archive-new-indicator" aria-label="Ready to learn">NEW</span>`
        : canProgress && learned
          ? `<span class="archive-upgrade-indicator" aria-label="Ready to improve">&#8593;</span>`
          : "";
    const selected =
      this.selectedTechniqueId === entry.techniqueId ? "selected" : "";
    const status = learned
      ? "Mastery level " + entry.currentLevel
      : "Not learned";
    const title = entry.name + " | " + status;
    return `<button type="button" class="archive-technique-icon ${learned ? "learned" : "unlearned"} ${selected}" style="--archive-rarity:${rarityColor(rarity)}" data-select-technique="${escapeHtml(entry.techniqueId)}" data-technique-name="${escapeHtml((entry.name + " " + entry.techniqueId).toLocaleLowerCase())}" data-technique-family="${escapeHtml(entry.family)}" data-level-rarity="${rarityForLevel(entry.currentLevel ?? 1)}" aria-label="${escapeHtml(entry.name + ", " + status)}" title="${escapeHtml(title)}">${techniqueIcon(entry.techniqueId, learned ? "technique-learned" : "technique-unlearned")}${indicator}</button>`;
  }
  private renderTechniqueDetails(): string {
    const view = this.view;
    const entry = view?.techniques.find(
      (item) => item.techniqueId === this.selectedTechniqueId,
    );
    if (!view || !entry)
      return `<div class="empty-state">Select a technique to view its details.</div>`;
    const technique = TECHNIQUES[entry.techniqueId];
    if (!technique)
      return `<div class="empty-state">Technique data is unavailable.</div>`;
    const next = entry.next;
    const learned = entry.currentLevel !== null;
    const levelRarity = learned
      ? rarityForLevel(entry.currentLevel!)
      : "common";
    const state = learned
      ? `Mastery level ${entry.currentLevel} / 10`
      : "Not learned";
    const nextLabel = next
      ? `Next level ${next.targetLevel} | ${rarityLabel(next.scroll.rarity)} scroll x ${next.scroll.requiredQuantity} | ${next.scroll.ownedQuantity} owned`
      : "Mastered at level 10";
    const scrolls = entry.scrolls.length
      ? entry.scrolls
          .map(
            (scroll) =>
              `<span class="archive-owned-scroll" style="--archive-rarity:${rarityColor(scroll.rarity)}">${escapeHtml(rarityLabel(scroll.rarity))} x ${scroll.ownedQuantity.toLocaleString()}</span>`,
          )
          .join("")
      : `<span class="archive-no-owned-scrolls">No exclusive scrolls in Warehouse.</span>`;
    const actionReason =
      !entry.canAdvance && entry.unavailableReason
        ? `<p class="archive-action-reason" role="status">${escapeHtml(entry.unavailableReason)}</p>`
        : !view.ninja.canProgress
          ? `<p class="archive-action-reason" role="status">${escapeHtml(view.ninja.unavailableReason)}</p>`
          : "";
    const actionLabel = next
      ? learned
        ? `Improve to level ${next.targetLevel}`
        : "Teach this technique"
      : "";
    const action = next
      ? `<button type="button" id="archive-progress-technique" class="button button-primary" ${entry.canAdvance && view.ninja.canProgress ? "" : "disabled"}>${actionLabel}</button>`
      : "";
    const rarityBorder = rarityColor(learned ? levelRarity : rarityForLevel(1));
    return `
      <header class="archive-detail-heading">
        <div class="archive-detail-leading">
          <div class="archive-detail-icon ${learned ? "learned" : "unlearned"}" style="--archive-rarity:${rarityBorder}">${techniqueIcon(entry.techniqueId)}</div>
          <div class="archive-detail-title"><span class="eyebrow">${escapeHtml(familyLabel(entry.family))} | ${escapeHtml(familyLabel(entry.proficiency))} | ${escapeHtml(targetingLabel(entry.targeting))}</span><h2>${escapeHtml(entry.name)}</h2><span class="archive-mastery" style="--archive-rarity:${rarityBorder}">${escapeHtml(state)} | ${escapeHtml(nextLabel)}</span></div>
        </div>
        ${action}
      </header>
      ${actionReason}
      <nav class="section-tabs archive-detail-tabs" role="tablist" aria-label="Technique details">
        <button id="archive-tab-information" class="section-tab" type="button" role="tab" aria-selected="true" aria-controls="archive-panel-information" tabindex="0" data-section-tab="information">Information</button>
        <button id="archive-tab-progression" class="section-tab" type="button" role="tab" aria-selected="false" aria-controls="archive-panel-progression" tabindex="-1" data-section-tab="progression">Level progression</button>
      </nav>
      <section id="archive-panel-information" class="archive-detail-tab-panel" role="tabpanel" tabindex="0" aria-labelledby="archive-tab-information" data-section-panel="information">
        <div class="archive-technique-description"><span class="eyebrow">ABOUT THIS TECHNIQUE</span><p>${escapeHtml(technique.description)}</p></div>
        <div class="archive-technique-facts"><article><span>Family</span><b>${escapeHtml(familyLabel(entry.family))}</b></article><article><span>Proficiency</span><b>${escapeHtml(familyLabel(entry.proficiency))}</b></article><article><span>Targeting</span><b>${escapeHtml(targetingLabel(entry.targeting))}</b></article></div>
        <div class="archive-detail-scrolls"><span class="eyebrow">OWNED EXCLUSIVE SCROLLS</span><div>${scrolls}</div></div>
      </section>
      <section id="archive-panel-progression" class="archive-detail-tab-panel" role="tabpanel" tabindex="0" aria-labelledby="archive-tab-progression" data-section-panel="progression" hidden>
        <div class="archive-progression-heading"><div><span class="eyebrow">LEVEL PROGRESSION</span><p>Scroll requirements and combat mechanics by mastery level.</p></div></div>
        <div class="archive-progression-table-wrap"><table class="archive-progression-table"><thead><tr><th>Level</th><th>Scroll</th><th>Chakra</th><th>Cooldown</th><th>Range</th><th>Targets</th><th>Power</th><th>Other mechanics</th></tr></thead><tbody>${TECHNIQUE_LEVELS.map((level) => this.renderProgressionRow(entry, level, technique.levels[level])).join("")}</tbody></table></div>
        <p class="archive-detail-costs">Ryo ${next?.canonicalResources.ryo.toLocaleString() ?? "0"} | Energy ${next?.canonicalResources.energy.toLocaleString() ?? "0"} <span>Free in this MVP</span></p>
      </section>`;
  }
  private renderProgressionRow(
    entry: TechniqueArchiveCatalogEntry,
    level: (typeof TECHNIQUE_LEVELS)[number],
    mechanics: TechniqueLevelParameters,
  ): string {
    const requirement = techniqueProgressionRequirement(
      level === 1 ? null : level - 1,
    );
    if (!requirement) return "";
    const owned =
      entry.scrolls.find((scroll) => scroll.rarity === requirement.scrollRarity)
        ?.ownedQuantity ?? 0;
    const state =
      entry.currentLevel === level
        ? "is-current"
        : entry.next?.targetLevel === level
          ? "is-next"
          : "";
    const color = rarityColor(requirement.scrollRarity);
    return `<tr class="${state}" data-level="${level}"><th scope="row"><span class="archive-level-chip" style="--archive-rarity:${color}">${level}</span></th><td><span class="archive-level-scroll" style="--archive-rarity:${color}">${escapeHtml(rarityLabel(requirement.scrollRarity))} x ${requirement.scrollQuantity}</span><small>Owned: ${owned.toLocaleString()}</small></td><td>${mechanics.chakraCost}</td><td>${seconds(mechanics.cooldownMs)}s</td><td>${mechanics.range}</td><td>${mechanics.maxTargets}</td><td>${mechanics.power} <small>x ${mechanics.attackCoefficient.toFixed(2)}</small></td><td>${escapeHtml(otherMechanics(mechanics))}</td></tr>`;
  }
  private applyFilters(): void {
    const query = this.techniqueSearch.trim().toLocaleLowerCase();
    let visible = 0;
    this.root
      .querySelectorAll<HTMLButtonElement>(".archive-technique-icon")
      .forEach((button) => {
        const matches =
          (button.dataset.techniqueName ?? "").includes(query) &&
          (!this.familyFilter ||
            button.dataset.techniqueFamily === this.familyFilter) &&
          (!this.levelRarityFilter ||
            button.dataset.levelRarity === this.levelRarityFilter);
        button.hidden = !matches;
        if (matches) visible++;
      });
    const empty = this.root.querySelector<HTMLElement>("#archive-filter-empty");
    if (empty) empty.hidden = visible > 0;
  }
  private confirmProgression(entry: TechniqueArchiveCatalogEntry): void {
    const next = entry.next;
    const ninja = this.selectedNinja();
    if (!next || !ninja) return;
    document.getElementById("technique-progress-confirm")?.remove();
    const dialog = document.createElement("dialog");
    dialog.id = "technique-progress-confirm";
    dialog.className = "technique-progress-dialog";
    const teach = entry.currentLevel === null;
    dialog.innerHTML = `<form method="dialog"><p class="eyebrow">CONFIRM ${teach ? "LEARNING" : "IMPROVEMENT"}</p><div class="archive-confirm-ninja"><div class="archive-confirm-portrait">${ninjaPortrait(ninja)}</div><div><h2>${escapeHtml(ninja.name)}</h2><span>${escapeHtml(rarityLabel(ninja.rarity))} | ${escapeHtml(ninja.rank)} | Lv ${ninja.level}</span></div></div><p>${teach ? "Teach" : "Improve"} <b>${escapeHtml(entry.name)}</b> for <b>${escapeHtml(ninja.name)}</b> to level <b>${next.targetLevel}</b>?</p><p class="muted">Consume ${next.scroll.requiredQuantity} ${escapeHtml(rarityLabel(next.scroll.rarity))} exclusive scroll(s). Ryo ${next.canonicalResources.ryo.toLocaleString()} and Energy ${next.canonicalResources.energy.toLocaleString()} are free in this MVP.</p><div class="technique-progress-dialog-actions"><button value="cancel" class="button button-outline">Cancel</button><button id="confirm-technique-progress" value="default" class="button button-primary">${teach ? "Teach technique" : "Improve technique"}</button></div></form>`;
    document.body.append(dialog);
    dialog.showModal();
    dialog.addEventListener("close", () => dialog.remove(), { once: true });
    dialog
      .querySelector<HTMLButtonElement>("#confirm-technique-progress")
      ?.addEventListener("click", (event) => {
        event.preventDefault();
        void this.progress(entry, dialog);
      });
  }
  private async progress(
    entry: TechniqueArchiveCatalogEntry,
    dialog: HTMLDialogElement,
  ): Promise<void> {
    const next = entry.next;
    if (!next) return;
    const button = dialog.querySelector<HTMLButtonElement>(
      "#confirm-technique-progress",
    );
    if (button) button.disabled = true;
    const operationKey = this.selectedNinjaId + ":" + entry.techniqueId;
    const key = this.pendingKeys.get(operationKey) ?? crypto.randomUUID();
    this.pendingKeys.set(operationKey, key);
    try {
      const result = await this.request<TechniqueProgressionResult>(
        `/ninjas/${encodeURIComponent(this.selectedNinjaId)}/techniques/${encodeURIComponent(entry.techniqueId)}/progress`,
        {
          method: "POST",
          headers: { "idempotency-key": key },
          body: JSON.stringify({ expectedLevel: entry.currentLevel ?? 0 }),
        },
      );
      this.pendingKeys.delete(operationKey);
      dialog.close();
      const ninja = this.selectedNinja();
      this.noticeText =
        entry.name +
        " level " +
        result.techniqueLevel +
        " taught to " +
        (ninja?.name ?? "the selected ninja") +
        ". Consumed " +
        result.consumedScroll.quantity +
        " " +
        rarityLabel(result.consumedScroll.rarity) +
        " scroll(s)." +
        (result.replayed ? " Confirmed after retry." : "");
      await this.load();
    } catch (error) {
      if (button) button.disabled = false;
      const notice = dialog.querySelector<HTMLElement>(
        ".technique-progress-error",
      );
      if (notice) notice.textContent = (error as Error).message;
      else {
        const message = document.createElement("p");
        message.className = "technique-progress-error";
        message.setAttribute("role", "alert");
        message.textContent = (error as Error).message;
        dialog.querySelector("form")?.append(message);
      }
    }
  }
}
